import React, { useState, useContext, useMemo } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";

import FormField from "../../components/form-components/FormField";
import t from "../../lib/translate";
import { Headline } from "../../components/Headline";

import * as styles from "./component.module.less";
import FormService from "../../lib/service/formService";
import { FormSent } from "../FormSent/FormSent";
import FormLayoutContext from "../../layouts/StandardFormLayout/context";
import { resolveSessionValues } from "../resolveSessionValues";
import FocusError from "../FocusError";

function ThermografieForm({
    formId,
    locale,
    data: { allCameras, allCameraAccessories, allOptics, selected },
    locationState = {},
    isSending
}) {
    const [isSent, setIsSent] = useState(false);
    const { setShowSubmit } = useContext(FormLayoutContext);

    const service = new FormService(
        process.env.GATSBY_FORM_MICROSERVICE_BASE_URL,
        locale
    );

    const submit = (userData) => {
        const formData = {
            ...userData,
            language: locale,
            url: locationState?.prevUrl ?? "",
            gdpr: userData.gdpr ? "1" : "0",
            newsletter: userData.newsletter ? "1" : "0",
            contactMe: userData.contactMe ? "1" : "0",
            moreInformation: userData.moreInformation ? "1" : "0"
        };

        const { campaign, pnr, dnr } = resolveSessionValues();

        if (itemData) {
            Object.keys(itemData).forEach((key) => {
                formData[key] = itemData[key];
            });
        }

        /* -------------------------------------------------------------------------- */
        const activeFilter = sessionStorage.getItem("cf_Active");

        if (activeFilter) {
            const filterParams = JSON.parse(activeFilter) ?? [];
            formData.filter = filterParams
                .map(({ key, options }) => {
                    return `${key}: ${options
                        .map(({ label }) => label)
                        .join(", ")}`;
                })
                .join("; ");
        }
        /* -------------------------------------------------------------------------- */

        if (campaign) {
            formData.campaign = campaign.name;
        }

        if (pnr) {
            formData.pnr = pnr;
        }

        if (dnr) {
            formData.dnr = dnr;
        }
        isSending(true);
        service
            .submit("thermography-form", formData)
            .then((res) => {
                if (res === null) {
                    window?.dataLayer?.push({
                        event: "form_successfully_submitted"
                    });
                    setIsSent(true);
                    setShowSubmit(false);
                    isSending(false);
                }
            })
            .catch((error) => {
                console.error(error);
                isSending(false);
            });
    };

    const itemData = useMemo(() => {
        if (!!locationState && !!locationState.camAndOptic) {
            let selectedData = {};

            const selectedCam = allCameras.find(
                ({ originalId }) =>
                    originalId === locationState.camAndOptic[0].cameraId
            );

            const selectedObj = allOptics.find(
                ({ originalId }) =>
                    originalId === locationState.camAndOptic[0].opticId
            );

            selectedData.camera = selectedCam.name;
            selectedData.distance = locationState.distance;
            selectedData.objektive = selectedObj.lens;
            selectedData.res =
                selectedObj.detectorWidth + " × " + selectedObj.detectorHeight;
            return selectedData;
        }

        const camera = allCameras.find(({ id }) => id === selected);

        if (camera) return { camera: camera.name };

        return allCameraAccessories.find(({ id }) => id === selected);
    }, [locationState, allCameras, allCameraAccessories]);

    return (
        <>
            {!isSent && (
                <Formik
                    initialValues={{
                        email: "",
                        salutation: "",
                        title: "",
                        firstname: "",
                        lastname: "",
                        company: "",
                        country: "",
                        zip: "",
                        city: "",
                        street: "",
                        number: "",
                        phone: "",
                        contactMe: "",
                        newsletter: "",
                        moreInformation: "",
                        message: "",
                        department: "",
                        application: "",
                        budget: "",
                        planToBuy: "",
                        gdpr: ""
                    }}
                    validationSchema={Yup.object({
                        email: Yup.string()
                            .email()
                            .required(t("form.thermography.email.error")),
                        salutation: Yup.string()
                            .required(t("form.thermography.salutation.error"))
                            .oneOf([
                                t("form.thermography.salutation.value-01"),
                                t("form.thermography.salutation.value-02"),
                                t("form.thermography.salutation.value-03"),
                                t("form.thermography.salutation.value-04")
                            ]),
                        title: Yup.string(),
                        firstname: Yup.string().required(
                            t("form.thermography.firstname.error")
                        ),
                        lastname: Yup.string().required(
                            t("form.thermography.surname.error")
                        ),
                        company: Yup.string().required(
                            t("form.thermography.company.error")
                        ),
                        country: Yup.string(),
                        zip: Yup.string().required(
                            t("form.thermography.zipcode.error")
                        ),
                        city: Yup.string().required(
                            t("form.thermography.city.error")
                        ),
                        street: Yup.string(),
                        number: Yup.string(),
                        phone: Yup.number().required(
                            t("form.thermography.phone.error")
                        ),
                        contactMe: Yup.boolean(),
                        newsletter: Yup.boolean(),
                        moreInformation: Yup.boolean(),
                        message: Yup.string(),
                        department: Yup.string(),
                        application: Yup.string(),
                        budget: Yup.string().oneOf([
                            t("form.thermography.budget.value-01"),
                            t("form.thermography.budget.value-02"),
                            t("form.thermography.budget.value-03"),
                            t("form.thermography.budget.value-04"),
                            t("form.thermography.budget.value-05"),
                            t("form.thermography.budget.value-06"),
                            t("form.thermography.budget.value-07")
                        ]),
                        planToBuy: Yup.string().oneOf([
                            t("form.thermography.ordertime.value-01"),
                            t("form.thermography.ordertime.value-02"),
                            t("form.thermography.ordertime.value-03"),
                            t("form.thermography.ordertime.value-04"),
                            t("form.thermography.ordertime.value-05"),
                            t("form.thermography.ordertime.value-06")
                        ]),
                        gdpr: Yup.boolean()
                            .oneOf(
                                [true],
                                t("form.thermography.privacypolicy.error")
                            )
                            .required(
                                t("form.thermography.privacypolicy.error")
                            )
                    })}
                    onSubmit={submit}
                >
                    <Form id={formId}>
                        <legend>
                            {/* <Tagline> {t("form.thermography.tagline")}</Tagline> TODO: Obsolet? */}
                            <Headline semanticOfHeadline="H1">
                                {t("form.thermography.headline")}
                            </Headline>
                            <div>{t("form.thermography.subheadline")}</div>
                        </legend>

                        {/*Static Data*/}
                        <div className={styles.formUnitSystemBlock}>
                            {/* <div className={styles.formUnitSystem}>
                                <span>Einheitensystem</span>
                            </div> */}

                            {itemData?.camera && (
                                <div className={styles.dataRow}>
                                    <span>Kamera</span>
                                    <span>{itemData?.camera}</span>
                                </div>
                            )}

                            {itemData?.objektive && (
                                <div className={styles.dataRow}>
                                    <span>Infrarot-Objektiv</span>
                                    <span>
                                        {itemData?.camera +
                                            " / " +
                                            itemData?.objektive}
                                    </span>
                                </div>
                            )}

                            {itemData?.res && (
                                <div className={styles.dataRow}>
                                    <span>Detektorauflösung</span>
                                    <span>{itemData?.res}</span>
                                </div>
                            )}

                            {itemData?.distance && (
                                <div className={styles.dataRow}>
                                    <span>Entfernung</span>
                                    <span>{itemData?.distance}</span>
                                </div>
                            )}
                        </div>

                        <FormField
                            type="text"
                            name="email"
                            label={t("form.thermography.email.label")}
                            placeholder={t("form.thermography.email.label")}
                            required
                        />
                        <FormField
                            type="select"
                            name="salutation"
                            label={t("form.thermography.salutation.label")}
                            placeholder={t(
                                "form.thermography.salutation.label"
                            )}
                            options={[
                                t("form.thermography.salutation.value-01"),
                                t("form.thermography.salutation.value-02"),
                                t("form.thermography.salutation.value-03"),
                                ...t(
                                    "form.thermography.salutation.value-04"
                                ).split(",")
                            ]}
                            required
                        />
                        <FormField
                            type="text"
                            name="title"
                            label={t("form.thermography.title.label")}
                            placeholder={t("form.thermography.title.label")}
                        />
                        <FormField
                            type="text"
                            name="firstname"
                            label={t("form.thermography.firstname.label")}
                            placeholder={t("form.thermography.firstname.label")}
                            required
                        />
                        <FormField
                            type="text"
                            name="lastname"
                            label={t("form.thermography.surname.label")}
                            placeholder={t("form.thermography.surname.label")}
                            required
                        />
                        <FormField
                            type="text"
                            name="company"
                            label={t("form.thermography.company.label")}
                            placeholder={t("form.thermography.company.label")}
                            required
                        />
                        <FormField
                            type="text"
                            name="country"
                            label={t("form.thermography.country.label")}
                            placeholder={t("form.thermography.country.label")}
                        />
                        <FormField
                            type="text"
                            name="zip"
                            label={t("form.thermography.zipcode.label")}
                            placeholder={t("form.thermography.zipcode.label")}
                            required
                        />
                        <FormField
                            type="text"
                            name="city"
                            label={t("form.thermography.city.label")}
                            placeholder={t("form.thermography.city.label")}
                            required
                        />
                        <FormField
                            type="text"
                            name="street"
                            label={t("form.thermography.street.label")}
                            placeholder={t("form.thermography.street.label")}
                        />
                        <FormField
                            type="text"
                            name="number"
                            label={t("form.thermography.housenumber.label")}
                            placeholder={t(
                                "form.thermography.housenumber.label"
                            )}
                        />
                        <FormField
                            type="text"
                            name="phone"
                            label={t("form.thermography.phone.label")}
                            placeholder={t("form.thermography.phone.label")}
                            required
                        />
                        <FormField
                            type="checkbox"
                            name="contactMe"
                            label={t("form.thermography.contact.checkbox")}
                            placeholder={t(
                                "form.thermography.contact.checkbox"
                            )}
                        />
                        <FormField
                            type="checkbox"
                            name="newsletter"
                            label={t("form.thermography.newsletter.checkbox")}
                            placeholder={t(
                                "form.thermography.newsletter.checkbox"
                            )}
                        />
                        <FormField
                            type="checkbox"
                            name="moreInformation"
                            label={t("form.thermography.information.checkbox")}
                            placeholder={t(
                                "form.thermography.information.checkbox"
                            )}
                        />
                        <FormField
                            type="textarea"
                            name="message"
                            label={t("form.thermography.yourmessage.label")}
                            placeholder={t(
                                "form.thermography.yourmessage.label"
                            )}
                        />
                        <FormField
                            type="text"
                            name="department"
                            label={t("form.thermography.department.label")}
                            placeholder={t(
                                "form.thermography.department.label"
                            )}
                        />
                        <FormField
                            type="text"
                            name="application"
                            label={t("form.thermography.measuringtask.label")}
                            placeholder={t(
                                "form.thermography.measuringtask.label"
                            )}
                        />
                        <FormField
                            type="select"
                            name="budget"
                            label={t("form.thermography.budget.label")}
                            placeholder={t("form.thermography.budget.label")}
                            options={[
                                t("form.thermography.budget.value-01"),
                                t("form.thermography.budget.value-02"),
                                t("form.thermography.budget.value-03"),
                                t("form.thermography.budget.value-04"),
                                t("form.thermography.budget.value-05"),
                                t("form.thermography.budget.value-06"),
                                ...t("form.thermography.budget.value-07").split(
                                    ","
                                )
                            ]}
                        />
                        <FormField
                            type="select"
                            name="planToBuy"
                            label={t("form.thermography.ordertime.label")}
                            placeholder={t("form.thermography.ordertime.label")}
                            options={[
                                t("form.thermography.ordertime.value-01"),
                                t("form.thermography.ordertime.value-02"),
                                t("form.thermography.ordertime.value-03"),
                                t("form.thermography.ordertime.value-04"),
                                t("form.thermography.ordertime.value-05"),
                                ...t(
                                    "form.thermography.ordertime.value-06"
                                ).split(",")
                            ]}
                        />
                        <FormField
                            type="checkbox"
                            name="gdpr"
                            label={t(
                                "form.thermography.privacypolicy.checkbox"
                            )}
                            placeholder={t(
                                "form.thermography.privacypolicy.checkbox"
                            )}
                            required
                        />
                        <div>{t("form.thermography.subtitle")}</div>
                        <FocusError />
                    </Form>
                </Formik>
            )}
            {isSent && <FormSent />}
        </>
    );
}

export { ThermografieForm };
